import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import api from '../services/api';
import {
  fetchProjectUpdates,
  createProjectUpdate,
  editProjectUpdate,
  deleteProjectUpdate,
  clearUpdates,
  clearError,
  fetchComments,
  createComment,
  editComment,
  deleteComment,
} from '../store/projectUpdatesSlice';

const ProjectProgressScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { user } = useSelector((state: RootState) => state.auth);
  const { updates, loading, error } = useSelector((state: RootState) => state.projectUpdates);

  const [project, setProject] = useState<any>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<any>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editingUpdateId, setEditingUpdateId] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [notifyClients, setNotifyClients] = useState(false);
  const [editNotifyClients, setEditNotifyClients] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    visible: boolean;
    url: string;
    type: string;
  }>({ visible: false, url: '', type: '' });
  const [mediaBlobUrls, setMediaBlobUrls] = useState<Record<string, string>>({});
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [newCommentText, setNewCommentText] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const isDesigner = user?.role === 'designer';
  const token = localStorage.getItem('token');

  const fetchProjectInfo = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectUpdates(projectId));

      // Fetch project info to check for clients
      fetchProjectInfo();
    }
    return () => {
      dispatch(clearUpdates());
    };
  }, [projectId, dispatch, fetchProjectInfo]);

  // Fetch and convert media to blob URLs (secure pattern like ProjectViewScreen)
  useEffect(() => {
    const fetchMediaBlobs = async () => {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const newBlobUrls: Record<string, string> = {};

      for (const update of updates) {
        if (update.mediaCount > 0) {
          for (let index = 0; index < update.media.length; index++) {
            const key = `${update._id}-${index}`;

            // Skip if already fetched
            if (mediaBlobUrls[key]) {
              newBlobUrls[key] = mediaBlobUrls[key];
              continue;
            }

            try {
              const response = await fetch(
                `${baseUrl}/project-updates/${update._id}/media/${index}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );

              if (response.ok) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                newBlobUrls[key] = blobUrl;
              }
            } catch (error) {
              console.error(`Failed to fetch media ${key}:`, error);
            }
          }
        }
      }

      setMediaBlobUrls(newBlobUrls);
    };

    if (updates.length > 0 && token) {
      fetchMediaBlobs();
    }

    // Cleanup blob URLs on unmount
    return () => {
      Object.values(mediaBlobUrls).forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updates, token]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles([...selectedFiles, ...files]);

    // Create preview URLs
    const newUrls = files.map((file) => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newUrls]);
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!notes.trim() || !projectId) {
      alert('Please enter notes');
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        createProjectUpdate({
          projectId,
          notes,
          files: selectedFiles,
          notifyClients,
        })
      ).unwrap();

      // Clean up
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      setNotes('');
      setSelectedFiles([]);
      setPreviewUrls([]);
      setNotifyClients(false);
      setShowAddModal(false);
    } catch (err: any) {
      alert(err || 'Failed to create update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (updateId: string) => {
    if (!window.confirm('Are you sure you want to delete this progress update?')) {
      return;
    }

    try {
      await dispatch(deleteProjectUpdate(updateId)).unwrap();
    } catch (err: any) {
      alert(err || 'Failed to delete update');
    }
  };

  const handleEdit = async () => {
    if (!editNotes.trim()) {
      alert('Please enter notes');
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(editProjectUpdate({ updateId: editingUpdateId, notes: editNotes.trim(), notifyClients: editNotifyClients })).unwrap();
      setShowEditModal(false);
      setEditingUpdate(null);
      setEditNotes('');
      setEditingUpdateId('');
      setEditNotifyClients(false);
    } catch (err: any) {
      alert(err || 'Failed to edit update');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (update: any) => {
    setEditingUpdate(update);
    setEditNotes(update.notes);
    setEditingUpdateId(update._id);
    setShowEditModal(true);
  };

  const openPreview = async (updateId: string, index: number, contentType: string) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

      // Fetch with authentication header (secure pattern)
      const response = await fetch(
        `${baseUrl}/project-updates/${updateId}/media/${index}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load media');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setPreviewModal({ visible: true, url: blobUrl, type: contentType });
    } catch (error) {
      console.error('Preview failed:', error);
      alert('Failed to load media preview');
    }
  };

  const closePreview = () => {
    // Clean up blob URL
    if (previewModal.url && previewModal.url.startsWith('blob:')) {
      URL.revokeObjectURL(previewModal.url);
    }
    setPreviewModal({ visible: false, url: '', type: '' });
  };

  // Comment handlers
  const toggleComments = (updateId: string) => {
    const currentlyExpanded = expandedComments[updateId];
    setExpandedComments(prev => ({ ...prev, [updateId]: !currentlyExpanded }));

    if (!currentlyExpanded) {
      // Load comments if not already loaded
      dispatch(fetchComments(updateId));
    }
  };

  const handleAddComment = async (updateId: string) => {
    if (!newCommentText.trim()) return;

    try {
      await dispatch(createComment({ projectUpdateId: updateId, text: newCommentText.trim() })).unwrap();
      setNewCommentText('');
    } catch (error) {
      alert('Failed to add comment');
    }
  };

  const handleEditComment = async (commentId: string, text: string) => {
    if (!text.trim()) return;

    try {
      await dispatch(editComment({ commentId, text: text.trim() })).unwrap();
      setEditingComment(null);
      setEditingText('');
    } catch (error) {
      alert('Failed to edit comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await dispatch(deleteComment(commentId)).unwrap();
    } catch (error) {
      alert('Failed to delete comment');
    }
  };

  const startEditing = (commentId: string, currentText: string) => {
    setEditingComment(commentId);
    setEditingText(currentText);
  };

  const cancelEditing = () => {
    setEditingComment(null);
    setEditingText('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Project Progress</h1>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back
            </button>
            {isDesigner && (
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                + Add Update
              </button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">Error: </span>
                <span className="ml-1">{error}</span>
              </div>
              <button
                onClick={() => dispatch(clearError())}
                className="text-red-600 hover:text-red-800 font-bold ml-4"
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && updates.length === 0 && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading updates...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && updates.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg mb-2">No progress updates yet</p>
            {isDesigner && (
              <p className="text-gray-500">Click "Add Update" to create your first update</p>
            )}
          </div>
        )}

        {/* Updates Timeline */}
        <div className="space-y-4">
          {[...updates].reverse().map((update) => (
            <div key={update._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-800">{update.designer.name}</h3>
                  <p className="text-sm text-gray-500">
                    {new Date(update.createdAt).toLocaleString()}
                  </p>
                </div>
                {isDesigner && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(update)}
                      className="text-blue-600 hover:text-blue-700 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(update._id)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              <p className="text-gray-700 mb-4 whitespace-pre-wrap">{update.notes}</p>

              {/* Media Grid */}
              {update.mediaCount > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {update.media.map((media, index) => {
                    const key = `${update._id}-${index}`;
                    const blobUrl = mediaBlobUrls[key];
                    const isVideo = media.contentType.startsWith('video/');

                    return (
                      <div
                        key={index}
                        onClick={() => openPreview(update._id, index, media.contentType)}
                        className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        {isVideo ? (
                          <div className="w-full h-full flex items-center justify-center bg-black">
                            <svg
                              className="w-12 h-12 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        ) : blobUrl ? (
                          <img
                            src={blobUrl}
                            alt="Media"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Comments Section */}
              <div className="mt-4 border-t pt-4">
                <button
                  onClick={() => toggleComments(update._id)}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                >
                  {expandedComments[update._id] ? 'Hide Comments' : `Comments (${update.commentsCount || 0})`}
                </button>

                {/* Recent Comment Preview */}
                {!expandedComments[update._id] && update.recentComment && (
                  <div className="mt-3 bg-gray-50 rounded-md p-3 border-l-4 border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-medium text-sm text-gray-700">
                        {update.recentComment.author?.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {update.recentComment.author?.role === 'designer' ? '(Designer)' : '(Client)'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(update.recentComment.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 italic">
                      "{update.recentComment.text.length > 120
                        ? `${update.recentComment.text.slice(0, 120)}...`
                        : update.recentComment.text}"
                    </p>
                  </div>
                )}

                {expandedComments[update._id] && (
                  <div className="mt-4 space-y-4">
                    {update.commentsLoading && (
                      <div className="text-center py-2">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Loading comments...</span>
                      </div>
                    )}

                    {!update.commentsLoading && update.comments && update.comments.map((comment: any) => (
                      <div key={comment._id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="font-medium text-sm text-gray-800">{comment.author.name}</span>
                              <span className="text-xs text-gray-500">
                                {comment.author.role === 'designer' ? '(Designer)' : '(Client)'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(comment.createdAt).toLocaleString()}
                              </span>
                            </div>

                            {editingComment === comment._id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={editingText}
                                  onChange={(e) => setEditingText(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                  rows={2}
                                />
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditComment(comment._id, editingText)}
                                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded-md hover:bg-gray-400"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.text}</p>
                            )}
                          </div>

                          {user && user.id === comment.author._id && editingComment !== comment._id && (
                            <div className="flex space-x-1 ml-2">
                              <button
                                onClick={() => startEditing(comment._id, comment.text)}
                                className="text-blue-600 hover:text-blue-700 text-xs"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment._id)}
                                className="text-red-600 hover:text-red-700 text-xs"
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Add Comment Form */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex space-x-2">
                        <textarea
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                          placeholder="Add a comment..."
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          rows={2}
                        />
                        <button
                          onClick={() => handleAddComment(update._id)}
                          disabled={!newCommentText.trim()}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Comment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Update Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">Add Progress Update</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes *
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={6}
                    placeholder="Describe the progress made..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Media (Optional)
                  </label>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />

                  {previewUrls.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {previewUrls.map((url, index) => (
                        <div key={index} className="relative">
                          <img
                            src={url}
                            alt="Preview"
                            className="w-full h-24 object-cover rounded"
                          />
                          <button
                            onClick={() => removeFile(index)}
                            className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Client Notification Checkbox */}
                {project?.clients && project.clients.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <input
                      id="notifyClients"
                      type="checkbox"
                      checked={notifyClients}
                      onChange={(e) => setNotifyClients(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="notifyClients" className="text-sm font-medium text-gray-700">
                      Notify clients of project update
                    </label>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNotes('');
                    previewUrls.forEach((url) => URL.revokeObjectURL(url));
                    setSelectedFiles([]);
                    setPreviewUrls([]);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={submitting || !notes.trim()}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Update Modal */}
        {showEditModal && editingUpdate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-4">Edit Progress Update</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes *
                  </label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={6}
                    placeholder="Describe the progress made..."
                    required
                  />
                </div>

                {/* Show existing media as read-only */}
                {editingUpdate.mediaCount > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Media (read-only)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {editingUpdate.media.map((media: any, index: number) => {
                        const key = `${editingUpdate._id}-${index}`;
                        const blobUrl = mediaBlobUrls[key];
                        const isVideo = media.contentType.startsWith('video/');

                        return (
                          <div key={index} className="relative">
                            {isVideo ? (
                              <div className="w-full h-20 bg-gray-200 flex items-center justify-center rounded">
                                <svg className="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                </svg>
                              </div>
                            ) : blobUrl ? (
                              <img
                                src={blobUrl}
                                alt="Media"
                                className="w-full h-20 object-cover rounded"
                              />
                            ) : (
                              <div className="w-full h-20 bg-gray-200 flex items-center justify-center rounded">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Media cannot be edited. Delete this update and create a new one if you need to change media.
                    </p>
                  </div>
                )}

                {/* Client Notification Checkbox */}
                {project?.clients && project.clients.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <input
                      id="editNotifyClients"
                      type="checkbox"
                      checked={editNotifyClients}
                      onChange={(e) => setEditNotifyClients(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="editNotifyClients" className="text-sm font-medium text-gray-700">
                      Notify clients of project update
                    </label>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingUpdate(null);
                    setEditNotes('');
                    setEditingUpdateId('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEdit}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  disabled={submitting || !editNotes.trim() || editNotes.trim() === editingUpdate.notes}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {previewModal.visible && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closePreview}
          >
            <div className="max-w-4xl max-h-[90vh] w-full p-4">
              <button
                onClick={closePreview}
                className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300"
              >
                ×
              </button>
              {previewModal.type.startsWith('video/') ? (
                <video src={previewModal.url} controls className="w-full h-auto max-h-[85vh]" />
              ) : (
                <img
                  src={previewModal.url}
                  alt="Preview"
                  className="w-full h-auto max-h-[85vh] object-contain"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectProgressScreen;
